import { type MouseEventHandler } from "react";

import { Menu, MenuButton, MenuItems, MenuItem, type MenuButtonProps } from "@headlessui/react";
import { useTranslations } from "next-intl";

import { Icon } from "@/components/icons/Icon";
import { Button, type Props as ButtonProps } from "@/components/sidebar/Button";
import { MENU_ITEM_FOCUS_RING_CLASS } from "@/components/sidebar/MenuItemContent";
import { twMerge } from "@/utils/twMerge";

import { useCreateHintHoverHandlers } from "../hints/useHintHover";

import type React from "react";

const ROW_KEYS: Partial<Record<string, string>> = { ArrowRight: "ArrowDown", ArrowLeft: "ArrowUp" };

/**
 * Lets Left/Right move through a menu laid out as a row. Headless UI menus only navigate with
 * Up/Down, so the key is re-sent as the matching one for the menu's own handler.
 */
function moveAlongRow(event: React.KeyboardEvent<HTMLElement>) {
  const key = ROW_KEYS[event.key];
  if (!key) return;
  event.preventDefault();
  event.stopPropagation();
  event.currentTarget.dispatchEvent(new KeyboardEvent("keydown", { key, bubbles: true }));
}

export type Item = Omit<ButtonProps<"button">, "as"> & {
  title?: string;
  title2?: string;
  tooltipContent?: string;
  icon: React.ReactNode;
  groupIndex?: number;
  onClick?: MouseEventHandler<HTMLButtonElement>;
};

type ItemsProps =
  | ({ items: Item[]; renderItemsAsVerticalList?: boolean } & Partial<Item>)
  | ({ items?: never; renderItemsAsVerticalList?: boolean } & Item);

type Props = {
  triggerProps?: MenuButtonProps;
  onItemSelect?: (item: Item) => void;
  itemsClassName?: string;
  nestedButtonClassName?: string;
  disableShifting?: boolean;
} & ItemsProps;

export function ComboButton({
  items = [],
  title,
  tooltipContent,
  icon,
  onClick,
  onItemSelect,
  disabled,
  triggerProps,
  renderItemsAsVerticalList,
  itemsClassName,
  nestedButtonClassName,
  disableShifting,
  hintKey,
  ...rest
}: Props) {
  const t = useTranslations("sidebar");
  const [firstItem] = items;
  const isCombo = items.length > 1;
  const createHintHandlers = useCreateHintHoverHandlers();

  const handleClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (onItemSelect) {
      onItemSelect(firstItem);
      return;
    }

    if (onClick) {
      onClick(e);
      return;
    }

    if (firstItem.onClick) {
      firstItem.onClick(e);
    }
  };

  const buttonProps = {
    ...rest,
    title: tooltipContent || title || firstItem?.title,
    tooltipContent: firstItem?.tooltipContent,
    icon: icon || firstItem?.icon,
    onClick: handleClick,
    "aria-label": title || firstItem?.title,
    disabled,
    inverted: true,
  };

  if (!isCombo) {
    return <Button {...buttonProps} />;
  }

  return (
    <Menu>
      {({ open }) => (
        <div className="contents">
          <div
            className={twMerge(
              "h-full w-full relative flex-shrink-0 max-w-9.5",
              !disabled && "group",
            )}
            {...(hintKey ? createHintHandlers(hintKey) : {})}
          >
            <Button
              {...buttonProps}
              icon={
                <div
                  className={twMerge(
                    "transition-transform duration-100 group-hover:-translate-x-[6px] motion-reduce:transition-none",
                    (disableShifting || open) && "-translate-x-[6px]",
                  )}
                >
                  {buttonProps.icon}
                </div>
              }
              className={twMerge(
                "group-hover:bg-gray-50 group-hover:dark:bg-black",
                !disabled && "hover:!bg-white hover:dark:!bg-gray-900",
                open && "bg-gray-50 dark:bg-black",
                buttonProps.className,
              )}
            />
            <MenuButton
              aria-label={t("moreOptions")}
              {...triggerProps}
              disabled={disabled}
              className={twMerge(
                "flex items-center justify-center absolute top-0 right-0 h-full w-[13px] border-l-[1px] border-transparent rounded-r-6",
                !disabled &&
                  !open &&
                  "group-hover:bg-gray-50 group-hover:dark:bg-black group-hover:border-gray-300 group-hover:dark:border-gray-600 hover:!bg-white hover:dark:!bg-gray-900",
                open && "bg-sidebar-button-active-bg border-gray-300 dark:border-gray-600",
              )}
            >
              <Icon
                name="ArrowDown"
                className={twMerge(
                  "[&>path]:stroke-document-text transition-transform motion-reduce:transition-none",
                  open && "translate-y-[1px]",
                )}
              />
            </MenuButton>
          </div>
          <MenuItems
            anchor={{ to: "bottom", padding: "10px", offset: "10px" }}
            onKeyDownCapture={renderItemsAsVerticalList ? undefined : moveAlongRow}
            className={twMerge(
              // The focused item shows by its highlight, as in other menus
              "z-50 flex flex-row p-1.25 rounded-12 bg-block-bg shadow-md dark:shadow-block-inverted outline-none",
              renderItemsAsVerticalList && "flex-col",
              itemsClassName,
            )}
          >
            {items.map((item, index) => {
              return (
                // eslint-disable-next-line react/no-array-index-key
                <MenuItem key={index}>
                  {renderItemsAsVerticalList ? (
                    <button
                      type="button"
                      aria-label={item.title}
                      onClick={item.onClick}
                      disabled={item.disabled}
                      className={twMerge(
                        "w-full h-8.5 px-2.5 text-sm flex items-center rounded-8 whitespace-nowrap tracking-tight",
                        MENU_ITEM_FOCUS_RING_CLASS,
                        !item.disabled && "data-[focus]:bg-sidebar-button-hover-bg",
                        item.disabled && "opacity-50",
                      )}
                    >
                      {item.icon && (
                        <div className="w-6 h-6 mr-2.5 [&>svg]:w-full [&>svg]:h-full ">
                          {item.icon}
                        </div>
                      )}
                      <span className="inline">
                        <span>{item.title}</span>
                        {item.title2 && (
                          <span className="text-input-placeholder">&nbsp;{item.title2}</span>
                        )}
                      </span>
                    </button>
                  ) : (
                    <Button
                      icon={item.icon}
                      title={item.title}
                      tooltipContent={item.tooltipContent}
                      hotkey={item.hotkey}
                      aria-label={item.title}
                      disabled={item.disabled}
                      className={twMerge(
                        "w-9.5 [&:not(:last-child)]:mr-1",
                        MENU_ITEM_FOCUS_RING_CLASS,
                        nestedButtonClassName,
                      )}
                      onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                        if (onItemSelect) {
                          onItemSelect(item);
                        } else if (item.onClick) {
                          item.onClick(e);
                        }
                      }}
                    />
                  )}
                </MenuItem>
              );
            })}
          </MenuItems>
        </div>
      )}
    </Menu>
  );
}
